import React, { useState } from "react";

import { useBroadcastEditContext } from "./BroadcastEditContainer";
import ErrorsContainer from "../BeefreeTemplates/ErrorsContainer";
import SaveAsBlueprintModal from "./SaveAsBlueprintModal";
import InlineEditField from "./InlineEditField";
import axios from "axios";
import { Badge, Button, Copy01, Modal, Tabs, Tooltip, Trash01 } from "@opendate-app/setlist";
import { toastError } from "../../../shared/toastHelper";

const styles = {
  headerRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
  },
  left: {
    flex: "1 1 320px",
    minWidth: 0,
  },
  name: {
    color: "var(--sl-text-primary)",
    fontSize: 16,
    fontWeight: 600,
  },
  subheader: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    color: "var(--sl-text-tertiary)",
    fontSize: 12,
    marginTop: 4,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
  },
  tabRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginTop: 16,
  },
  tabs: {
    flex: "1 1 320px",
    minWidth: 0,
  },
};

const actionTabs = ["overview", "content", "preview", "details", "sent"];
const editingTabs = ["content", "preview"];

const BroadcastEditHeader = () => {
  const {
    csrfToken,
    team,
    venueOwnership,
    broadcast,
    subject,
    template,
    setTestModalOpen,
    tab,
    setTab,
    scheduledSend,
    setScheduledSendPaneOpen,
    updateBroadcast,
    isAutosaving,
    previouslySent,
    sentAt,
    overviewMetrics,
    formatDate,
    createBlueprint,
    updateBlueprint,
    selectedSendDomain,
    availableSendDomains,
  } = useBroadcastEditContext();

  const [name, setName] = useState(broadcast.name);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const updateName = () => {
    updateBroadcast({
      broadcast: {
        name: name,
      }
    });
  };

  const handleSave = (alert = true, callback = null) => {
    updateBroadcast({
      broadcast: {
        subject: subject,
        beefree_template_attributes: {
          id: broadcast.beefree_template.id,
          template: template,
        }
      }
    }, alert, callback)
  };

  const deleteBroadcast = async () => {
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      await axios.delete(`/teams/${team.id}/broadcasts/${broadcast.id}`);
      window.location.href = `/teams/${team.id}/broadcasts`;
    } catch (error) {
      const errors = error.response?.data?.errors;
      toastError(errors?.length ? errors.join(", ") : "Could not delete broadcast, please try again");
      setConfirming(null);
    }
  };

  const duplicateBroadcast = async () => {
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const { data } = await axios.post(`/teams/${team.id}/broadcasts/${broadcast.id}/duplicate`);
      window.location.href = `/teams/${team.id}/broadcasts/${data.id}/edit`;
    } catch (error) {
      const errors = error.response?.data?.errors;
      toastError(errors?.length ? errors.join(", ") : "Could not duplicate broadcast, please try again");
      setConfirming(null);
    }
  };

  const headerTabs = [
    ...(previouslySent ? [{ value: "overview", label: "Overview" }] : []),
    { value: "content", label: "Content" },
    { value: "preview", label: "Preview" },
    ...(scheduledSend || previouslySent ? [{ value: "details", label: "Details" }] : []),
    ...(previouslySent ? [{ value: "sent", label: "Sent" }] : []),
  ];

  const canEditContent = !["overview", "details", "sent"].includes(tab);
  const canManageScheduled = scheduledSend && tab !== "details";
  const hasNoSendDomains = availableSendDomains.length === 0;

  return (
    <div>
      <div style={styles.headerRow}>
        <div style={styles.left}>
          <div style={styles.name}>
            <InlineEditField
              label={`Broadcasts / ${venueOwnership.venueable.name} /`}
              fieldName="broadcast name"
              value={name}
              onChange={setName}
              onSave={updateName}
              onCancel={() => setName(broadcast.name)}
            />
          </div>
          {[...actionTabs, "twilio-editor"].includes(tab) ? (
            <div style={styles.subheader}>
              {previouslySent && sentAt ? (
                <>
                  <Badge color="success" size="sm" dot>Sent</Badge>
                  <span>
                    {formatDate(sentAt)}
                    {overviewMetrics ? ` to ${overviewMetrics.sent_count.toLocaleString()} recipient${overviewMetrics.sent_count === 1 ? "" : "s"}` : ""}
                  </span>
                </>
              ) : !!scheduledSend ? (
                <>
                  <span>Scheduled for {formatDate(scheduledSend.scheduled_at)}</span>
                  <Button variant="link-color" size="sm" onClick={() => setScheduledSendPaneOpen(true)}>Edit</Button>
                </>
              ) : (
                "Not Scheduled"
              )}
            </div>
          ) : null}
        </div>
        {actionTabs.includes(tab) ? (
          <div style={styles.actions}>
            <Button
              as="a"
              variant="link-color"
              href={scheduledSend ? `/teams/${team.id}/broadcasts` : `/teams/${team.id}/broadcasts/${broadcast.id}/edit?tab=content-select`}
            >
              Back
            </Button>
            {canManageScheduled ? (
              <Tooltip title="Delete Broadcast">
                <Button variant="destructive" icon={Trash01} aria-label="Delete broadcast" onClick={() => setConfirming("delete")} />
              </Tooltip>
            ) : null}
            {canManageScheduled ? (
              <Tooltip title="Duplicate Broadcast">
                <Button variant="secondary-color" icon={Copy01} aria-label="Duplicate broadcast" onClick={() => setConfirming("duplicate")} />
              </Tooltip>
            ) : null}
            {canEditContent ? (
              <Button onClick={() => setShowBlueprintModal(true)}>
                Save as Blueprint
              </Button>
            ) : null}
            {canEditContent && scheduledSend ? (
              <Button variant="secondary-color" disabled={isAutosaving} onClick={() => handleSave()}>
                {isAutosaving ? "Saving…" : "Save"}
              </Button>
            ) : canEditContent ? (
              <Button
                variant="primary"
                disabled={isAutosaving}
                onClick={() => {
                  handleSave(false, () => {
                    window.location = `/teams/${team.id}/broadcasts/${broadcast.id}/edit?tab=review-and-schedule`;
                  });
                }}
              >
                {isAutosaving ? "Saving…" : "Save"}
              </Button>
            ) : null}
            {canManageScheduled ? (
              <Button variant="primary" onClick={() => setScheduledSendPaneOpen(true)}>
                Edit Schedule
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {actionTabs.includes(tab) ? (
        <div style={styles.tabRow}>
          <div style={styles.tabs}>
            <Tabs items={headerTabs} value={tab} onChange={setTab} />
          </div>
          {editingTabs.includes(tab) ? (
            <div style={styles.actions}>
              <ErrorsContainer templatableContext={useBroadcastEditContext} />
              {selectedSendDomain ? (
                <Button variant="secondary-color" onClick={() => setTestModalOpen(true)}>
                  Send Test
                </Button>
              ) : (
                <>
                  {hasNoSendDomains ? (
                    <Button as="a" variant="link-color" href={`/teams/${team.id}/marketing_settings?venue_ownership_id=${venueOwnership.id}&tab=email`}>
                      Set up a send domain
                    </Button>
                  ) : (
                    <Button variant="link-color" onClick={() => setTab("from")}>
                      Choose a send domain
                    </Button>
                  )}
                  <Tooltip
                    title={hasNoSendDomains ? "No send domain is configured for this team." : "Choose a send domain on the From tab before sending a test."}
                  >
                    <Button variant="secondary-color" disabled>
                      Send Test
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
      <Modal
        open={confirming === "delete"}
        onClose={() => setConfirming(null)}
        size="sm"
        type="error"
        icon
        title="Delete Broadcast?"
        description="You're about to delete this broadcast. This cannot be undone. Are you sure?"
        confirmLabel="Delete"
        onConfirm={deleteBroadcast}
      />
      <Modal
        open={confirming === "duplicate"}
        onClose={() => setConfirming(null)}
        size="sm"
        type="brand"
        icon
        title="Duplicate Broadcast?"
        description="You're about to duplicate this broadcast. Are you sure?"
        confirmLabel="Duplicate"
        onConfirm={duplicateBroadcast}
      />
      <SaveAsBlueprintModal
        isOpen={showBlueprintModal}
        onClose={() => setShowBlueprintModal(false)}
        template={template}
        copiedFromBlueprint={broadcast.beefree_template?.copied_from_blueprint}
        teamId={team.id}
        createBlueprint={createBlueprint}
        updateBlueprint={updateBlueprint}
      />
    </div>
  );
};

export default BroadcastEditHeader;
